import * as React from "react";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import { cn } from "../../lib/utils";

/* ---------------------------------------------------------- PageContainer */

const widths = {
  sm: "max-w-3xl",
  md: "max-w-5xl",
  lg: "max-w-7xl",
  full: "max-w-none",
};

export interface PageContainerProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: keyof typeof widths;
}

/** Standard horizontal gutters + max width for a page body */
export const PageContainer: React.FC<PageContainerProps> = ({ size = "lg", className, ...props }) => (
  <div className={cn("mx-auto w-full px-4 py-6 sm:px-6 lg:px-8", widths[size], className)} {...props} />
);

/* ------------------------------------------------------------- PageHeader */

export interface PageHeaderProps {
  title: React.ReactNode;
  description?: React.ReactNode;
  /** @deprecated Not rendered: the sidebar already says where you are */
  eyebrow?: React.ReactNode;
  actions?: React.ReactNode;
  /** Back link rendered above the title */
  backTo?: string;
  backLabel?: string;
  /** Rendered under the title row — tabs, filters */
  children?: React.ReactNode;
  className?: string;
  size?: PageContainerProps["size"];
  /** Extra row under the description (key facts, badges); block content allowed */
  meta?: React.ReactNode;
  /** Shows a skeleton in place of the title and description */
  loading?: boolean;
}

/** White header band that opens every app page */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  description,
  actions,
  backTo,
  backLabel = "Back",
  children,
  className,
  size = "lg",
  meta,
  loading,
}) => (
  <header className={cn(children && "border-b border-gray-200", className)}>
    <div className={cn("mx-auto w-full px-4 sm:px-6 lg:px-8", widths[size], children ? "pt-6" : "pt-6 sm:pt-8")}>
      {backTo && (
        <Link
          to={backTo}
          className="mb-3 inline-flex items-center gap-1 text-meta font-medium text-gray-500 transition-colors hover:text-gray-900"
        >
          <ChevronLeft className="size-4" />
          {backLabel}
        </Link>
      )}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold leading-8 tracking-[-0.02em] text-gray-900 sm:text-page sm:leading-9">
            {loading ? (
              <span aria-hidden className="block h-8 w-64 max-w-full animate-shimmer rounded-md bg-gray-100" />
            ) : (
              title
            )}
          </h1>
          {loading ? (
            <div aria-hidden className="mt-2 h-4 w-80 max-w-full animate-shimmer rounded-md bg-gray-100" />
          ) : (
            description && <div className="mt-1 max-w-2xl text-sm text-gray-500">{description}</div>
          )}
          {meta && <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-600">{meta}</div>}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
      {children && <div className="mt-4">{children}</div>}
    </div>
  </header>
);

/* -------------------------------------------------------------- PageTitle */

export interface PageTitleProps extends React.HTMLAttributes<HTMLDivElement> {
  title: string;
  actions?: React.ReactNode;
}

/** Compact header kept for existing call sites; new pages should use PageHeader */
export const PageTitle = React.forwardRef<HTMLDivElement, PageTitleProps>(
  ({ title, actions, className, ...props }, ref) => (
    <header
      ref={ref}
      className={cn("border-b border-gray-200 bg-white px-4 py-5 sm:px-6 lg:px-8", className)}
      {...props}
    >
      <div className="flex items-center justify-between gap-4">
        <h1 className="truncate text-xl font-semibold tracking-tight text-gray-900 sm:text-2xl">{title}</h1>
        {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
      </div>
    </header>
  )
);
PageTitle.displayName = "PageTitle";

/* ---------------------------------------------------------- DescriptionList */

export interface DescriptionItem {
  label: React.ReactNode;
  value: React.ReactNode;
  /** Let a long value wrap instead of truncating (e.g. remarks) */
  wrap?: boolean;
  /** Span the full row */
  full?: boolean;
}

/** Two-column key/value grid for facts like beds, baths, year built */
export const DescriptionList: React.FC<{
  items: DescriptionItem[];
  className?: string;
  columns?: 2 | 3 | 4;
  /** Wrap every value instead of truncating */
  wrap?: boolean;
}> = ({ items, className, columns = 3, wrap }) => (
  <dl
    className={cn(
      "grid grid-cols-2 gap-x-6 gap-y-4",
      columns === 3 && "sm:grid-cols-3",
      columns === 4 && "sm:grid-cols-4",
      className
    )}
  >
    {items.map((item, i) => (
      <div key={i} className={cn("min-w-0", item.full && "col-span-full")}>
        <dt className="text-meta text-gray-500">{item.label}</dt>
        <dd
          className={cn(
            "tabular mt-1 text-sm font-medium text-gray-900",
            wrap || item.wrap ? "whitespace-pre-line break-words" : "truncate"
          )}
        >
          {item.value ?? "—"}
        </dd>
      </div>
    ))}
  </dl>
);
